import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtDateTime } from '@/utils/dateFmt';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarCheck, CheckCircle, CaretLeft, CaretRight, Spinner, XCircle } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Markdown } from '@/components/Markdown';
import CountryCodeSelect from '@/components/CountryCodeSelect';
import { WithdrawalWaiverCheckbox } from '@/components/WithdrawalWaiverCheckbox';
import { PublicFooter } from '@/components/PublicFooter';
import { withErs, ERS_LABEL } from '@/config/legal';

const API = process.env.REACT_APP_BACKEND_URL;
const WD = ['Dom', '2ª', '3ª', '4ª', '5ª', '6ª', 'Sáb'];
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_FULL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const CAL_DOW = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const pad2 = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fmtDay = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return { wd: WD[d.getDay()], day: d.getDate(), mon: MONTHS[d.getMonth()] };
};

export default function BookingPage() {
  const { slug } = useParams();
  const [step, setStep] = useState(1);
  const [nutris, setNutris] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [nutri, setNutri] = useState(null);
  const [days, setDays] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selDate, setSelDate] = useState('');
  const [selTime, setSelTime] = useState('');
  const [viewMonth, setViewMonth] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [form, setForm] = useState({ name: '', email: '', phone: '', country_code: '+351', program: '', dum: '', delivery_date: '', modality: 'presencial' });
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null); // {status, booking}
  const [polling, setPolling] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState('');
  const [policyOpen, setPolicyOpen] = useState(false);
  const [programDescriptions, setProgramDescriptions] = useState({});
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [waiverConsent, setWaiverConsent] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [consentVersion, setConsentVersion] = useState('');
  const [waiverVersion, setWaiverVersion] = useState('');

  // ── Sessão gratuita via código de parceria (fluxo aditivo, não toca no fluxo pago acima) ──
  const [mode, setMode] = useState('paid'); // 'paid' | 'free'
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [partnershipCode, setPartnershipCode] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [freeNutris, setFreeNutris] = useState([]);
  const [freeForm, setFreeForm] = useState({ name: '', email: '', phone: '', country_code: '+351' });
  const [freeSubmitting, setFreeSubmitting] = useState(false);
  const [freeConfirm, setFreeConfirm] = useState(null);
  const [freePrivacyConsent, setFreePrivacyConsent] = useState(false);
  const validateCode = async () => {
    const code = (partnershipCode || '').trim().toUpperCase();
    if (!code) return;
    setCodeChecking(true); setCodeError('');
    try {
      const { data } = await axios.get(`${API}/api/public/partnership-codes/${code}/validate`);
      if (!data.valid) { setCodeError(data.reason || 'Código inválido'); setCodeChecking(false); return; }
      setPartnerName(data.partner_name);
      const { data: nutrisData } = await axios.get(`${API}/api/public/free-session/nutritionists`);
      setFreeNutris(nutrisData || []);
      setMode('free'); setStep(1);
    } catch { setCodeError('Não foi possível validar o código'); }
    finally { setCodeChecking(false); }
  };

  const cancelFreeMode = () => { setMode('paid'); setShowCodeInput(false); setPartnershipCode(''); setCodeError(''); setNutri(null); setStep(1); };

  const pickFreeNutri = async (n) => {
    setNutri(n); setStep(2); setLoadingSlots(true); setSelDate(''); setSelTime('');
    try {
      const { data } = await axios.get(`${API}/api/public/free-session/slots?nutri_id=${n.nutri_id}&days=28`);
      const list = data.days || [];
      setDays(list);
      if (list.length) { const fd = new Date(list[0].date + 'T00:00:00'); setViewMonth(new Date(fd.getFullYear(), fd.getMonth(), 1)); }
    } catch { toast.error('Erro ao carregar horários'); } finally { setLoadingSlots(false); }
  };

  const submitFree = async () => {
    if (!freeForm.name || !freeForm.email || !freeForm.phone) { toast.error('Preenche todos os campos'); return; }
    if (!freePrivacyConsent) { toast.error('Tens de aceitar os Termos de Serviço e a Política de Privacidade'); return; }
    setFreeSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/free-session/book`, {
        code: partnershipCode, nutri_id: nutri.nutri_id, date: selDate, time: selTime,
        ...freeForm, privacy_consent: freePrivacyConsent, consent_version: consentVersion,
      });
      setFreeConfirm(data); setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível confirmar a sessão'); }
    finally { setFreeSubmitting(false); }
  };
  // ── fim do fluxo aditivo ──

  // Return from Stripe
  const checkReturn = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const cancelId = params.get('cancel');
    if (cancelId) {
      setStep(6);
      try {
        const { data } = await axios.post(`${API}/api/public/booking/cancel`, { appointment_id: cancelId });
        setConfirm({ cancelled: true, status: data.status, date: data.date, time: data.time });
      } catch { setConfirm({ cancelled: true, status: 'error' }); }
      window.history.replaceState({}, '', '/agendarconsulta');
      return;
    }
    if (params.get('cancelled')) { toast.error('Pagamento cancelado. O horário continua disponível.'); window.history.replaceState({}, '', '/agendarconsulta'); return; }
    const sid = params.get('session_id');
    if (!sid) return;
    setPolling(true); setStep(5);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/api/public/booking/status/${sid}`);
        if (data.payment_status === 'paid') { setConfirm(data); setPolling(false); window.history.replaceState({}, '', '/agendarconsulta'); return; }
        if (data.status === 'expired') { toast.error('A sessão de pagamento expirou.'); setPolling(false); setStep(1); return; }
      } catch {}
      if (attempts >= 8) { setPolling(false); setConfirm({ payment_status: 'pending' }); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/booking/nutritionists`);
        const list = data.nutritionists || [];
        setNutris(list); setPrograms(data.programs || []);
        setCancellationPolicy(data.cancellation_policy || '');
        setProgramDescriptions(data.program_descriptions || {});
        setWaiverText(data.withdrawal_waiver_text || '');
        setConsentVersion(`${data.terms_and_conditions_updated_at || ''}|${data.privacy_policy_updated_at || ''}`);
        setWaiverVersion(data.withdrawal_waiver_text_updated_at || 'default');
        // Link direto por nutricionista: /agendarconsulta/<slug> salta a escolha de profissional
        if (slug) {
          const s = slug.toLowerCase();
          let match = list.find(n => (n.custom_slug || '').toLowerCase() === s);
          if (!match) { const byName = list.filter(n => (n.name || '').toLowerCase().split(/\s+/).includes(s)); if (byName.length === 1) match = byName[0]; }
          if (match) pickNutri(match);
        }
      } catch {}
    })();
    checkReturn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkReturn, slug]);

  const pickNutri = async (n) => {
    setNutri(n); setStep(2); setLoadingSlots(true); setSelDate(''); setSelTime('');
    try {
      const { data } = await axios.get(`${API}/api/public/booking/slots?nutri_id=${n.nutri_id}&days=62`);
      const list = data.days || [];
      setDays(list);
      if (list.length) { const fd = new Date(list[0].date + 'T00:00:00'); setViewMonth(new Date(fd.getFullYear(), fd.getMonth(), 1)); }
    }
    catch { toast.error('Erro ao carregar horários'); } finally { setLoadingSlots(false); }
  };

  const goToForm = () => { if (selDate && selTime) setStep(3); };

  const PEDIATRIC_PROGRAMS = ['Super Bebés', 'Nutrição Pediátrica'];
  const isPediatric = PEDIATRIC_PROGRAMS.includes(form.program || programs[0]);
  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error('Preenche todos os campos'); return; }
    if (isPediatric && !(form.child_name || '').trim()) { toast.error('Indica o nome da criança'); return; }
    if (!privacyConsent) { toast.error('Tens de aceitar os Termos de Serviço e a Política de Privacidade'); return; }
    if (!waiverConsent) { toast.error('Tens de aceitar a renúncia ao direito de livre resolução de 14 dias'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/booking/checkout`, {
        nutri_id: nutri.nutri_id, date: selDate, time: selTime, ...form, child_name: isPediatric ? (form.child_name || '').trim() : '',
        program: form.program || (programs[0] || 'Geral'), origin_url: window.location.origin,
        privacy_consent: privacyConsent, consent_version: consentVersion, waiver_version: waiverVersion,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível iniciar o pagamento');
      setSubmitting(false);
    }
  };

  const accent = nutri?.color || '#5B8C7B';
  const dayObj = days.find(d => d.date === selDate);
  const availSet = new Set(days.map(d => d.date));
  const slotCountByDate = {};
  days.forEach(d => { slotCountByDate[d.date] = (d.slots || []).length; });
  const now0 = new Date(); now0.setHours(0, 0, 0, 0);
  const minMonth = new Date(now0.getFullYear(), now0.getMonth(), 1);
  const maxMonth = new Date(now0.getFullYear(), now0.getMonth() + 2, 1);
  const canPrev = viewMonth > minMonth;
  const canNext = viewMonth < maxMonth;
  const monthCells = (() => {
    const y = viewMonth.getFullYear(), m = viewMonth.getMonth();
    const firstDow = (new Date(y, m, 1).getDay() + 6) % 7;
    const dim = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let dd = 1; dd <= dim; dd++) cells.push(new Date(y, m, dd));
    return cells;
  })();
  const goMonth = (delta) => setViewMonth(v => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="booking-page">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#2E3A33]">
            <CalendarCheck size={26} weight="duotone" style={{ color: accent }} />
            <h1 className="font-heading font-semibold text-2xl">Marcar consulta</h1>
          </div>
          <p className="text-sm text-[#7A8278] mt-1">{withErs(nutri?.name ? `${nutri.name} · Nutrição` : 'Ana Magalhães Pereira · Nutrição')}</p>
        </div>

        {/* STEP 5 — Confirmation */}
        {step === 5 && (
          <div className="bg-white rounded-3xl border border-[#E8E3DB] p-8 text-center" data-testid="booking-confirmation">
            {mode === 'free' ? (
              freeConfirm ? (
                <>
                  <CheckCircle size={56} weight="duotone" className="mx-auto text-green-600" />
                  <h2 className="font-heading text-xl text-[#2E3A33] mt-3">Sessão gratuita confirmada!</h2>
                  <p className="text-sm text-[#7A8278] mt-2" data-testid="free-confirm-details">{fmtDay(freeConfirm.date).wd}, {fmtDay(freeConfirm.date).day} {fmtDay(freeConfirm.date).mon} às {freeConfirm.time} · Sessão Nutrir a Super Mãe</p>
                  <p className="text-xs text-[#A8AFA3] mt-4">Enviámos os detalhes para o teu email. Até breve! 💚</p>
                </>
              ) : null
            ) : polling ? (
              <><Spinner size={40} className="mx-auto animate-spin text-[#5B8C7B]" /><p className="mt-4 text-[#2E3A33] font-medium">A confirmar o pagamento...</p></>
            ) : confirm?.payment_status === 'paid' ? (
              <>
                <CheckCircle size={56} weight="duotone" className="mx-auto text-green-600" />
                <h2 className="font-heading text-xl text-[#2E3A33] mt-3">Consulta confirmada!</h2>
                {confirm.booking && <p className="text-sm text-[#7A8278] mt-2" data-testid="confirm-details">{fmtDay(confirm.booking.date).wd}, {fmtDay(confirm.booking.date).day} {fmtDay(confirm.booking.date).mon} às {confirm.booking.time} · {confirm.booking.program}</p>}
                <p className="text-xs text-[#A8AFA3] mt-4">Enviámos a confirmação para o teu email. Até breve! 💚</p>
              </>
            ) : (
              <>
                <Spinner size={40} className="mx-auto text-amber-500" />
                <p className="mt-4 text-[#2E3A33]">O pagamento está a ser processado. Vais receber a confirmação por email.</p>
              </>
            )}
            <button onClick={() => { setStep(1); setNutri(null); setConfirm(null); setFreeConfirm(null); cancelFreeMode(); }} className="mt-6 text-sm text-[#5B8C7B] hover:underline" data-testid="booking-back-home-btn">Voltar ao início</button>
          </div>
        )}

        {/* STEP 6 — Cancellation result */}
        {step === 6 && (
          <div className="bg-white rounded-3xl border border-[#E8E3DB] p-8 text-center" data-testid="booking-cancel-result">
            {confirm?.cancelled && confirm.status !== 'error' ? (
              <>
                <XCircle size={56} weight="duotone" className="mx-auto text-[#b91c1c]" />
                <h2 className="font-heading text-xl text-[#2E3A33] mt-3">Consulta cancelada</h2>
                {confirm.date && <p className="text-sm text-[#7A8278] mt-2">{fmtDateTime(confirm.date, confirm.time)}</p>}
                <p className="text-xs text-[#A8AFA3] mt-4">Enviámos a confirmação do cancelamento para o teu email.</p>
              </>
            ) : (
              <><XCircle size={48} className="mx-auto text-amber-500" /><p className="mt-4 text-[#2E3A33]">Não foi possível cancelar. Contacta-nos por email.</p></>
            )}
            <button onClick={() => { setStep(1); setConfirm(null); }} className="mt-6 text-sm text-[#5B8C7B] hover:underline">Voltar ao início</button>
          </div>
        )}

        {/* STEP 1 — Choose nutritionist */}
        {step === 1 && mode === 'paid' && (
          <div className="space-y-3" data-testid="booking-step-nutri">
            <p className="text-sm text-[#7A8278] mb-2">Escolhe a nutricionista:</p>
            {nutris.length === 0 && <p className="text-center text-[#A8AFA3] py-8">Sem disponibilidade no momento.</p>}
            {nutris.map(n => (
              <button key={n.nutri_id} onClick={() => pickNutri(n)} data-testid={`booking-nutri-${n.nutri_id}`}
                className="w-full text-left bg-white rounded-2xl border border-[#E8E3DB] p-4 hover:shadow-md transition-shadow flex items-center gap-4">
                <span className="w-12 h-12 rounded-full flex items-center justify-center text-white font-heading font-semibold text-lg flex-shrink-0" style={{ backgroundColor: n.color }}>{n.name.charAt(0)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-[#2E3A33]">{n.name}</p>
                  {/Ana Magalhães Pereira/i.test(n.name) && <p className="text-[11px] text-[#7A8278]">{ERS_LABEL}</p>}
                  {n.welcome_message && <p className="text-xs text-[#7A8278] truncate">{n.welcome_message}</p>}
                </div>
                <span className="text-sm font-semibold flex-shrink-0" style={{ color: n.color }}>{n.booking_price}€</span>
              </button>
            ))}
            <div className="pt-2 text-center" data-testid="partnership-code-toggle-wrap">
              {!showCodeInput ? (
                <button onClick={() => setShowCodeInput(true)} className="text-xs text-[#7A8278] underline hover:text-[#5B8C7B]" data-testid="show-partnership-code-btn">Tenho um código de parceria</button>
              ) : (
                <div className="bg-white rounded-2xl border border-[#E8E3DB] p-4 mt-2 text-left" data-testid="partnership-code-panel">
                  <Label className="text-xs text-[#7A8278] mb-1 block">Código de parceria</Label>
                  <div className="flex gap-2">
                    <Input value={partnershipCode} onChange={e => { setPartnershipCode(e.target.value.toUpperCase()); setCodeError(''); }} placeholder="ex: CPP" className="rounded-xl flex-1" data-testid="partnership-code-input" />
                    <button onClick={validateCode} disabled={codeChecking || !partnershipCode.trim()} className="rounded-xl px-4 text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: accent }} data-testid="validate-partnership-code-btn">{codeChecking ? '...' : 'Validar'}</button>
                  </div>
                  {codeError && <p className="text-xs text-red-500 mt-1.5" data-testid="partnership-code-error">{codeError}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 1 (modo sessão gratuita) — Escolher nutricionista para a sessão gratuita */}
        {step === 1 && mode === 'free' && (
          <div className="space-y-3" data-testid="booking-step-free-nutri">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-2 text-sm text-[#2E3A33]">
              <strong>Sessão Nutrir a Super Mãe</strong> · 20 min · Grátis · Código {partnershipCode} ({partnerName})
            </div>
            <p className="text-sm text-[#7A8278] mb-2">Escolhe a nutricionista:</p>
            {freeNutris.length === 0 && <p className="text-center text-[#A8AFA3] py-8">Sem disponibilidade para sessões gratuitas no momento.</p>}
            {freeNutris.map(n => (
              <button key={n.nutri_id} onClick={() => pickFreeNutri(n)} data-testid={`free-booking-nutri-${n.nutri_id}`}
                className="w-full text-left bg-white rounded-2xl border border-[#E8E3DB] p-4 hover:shadow-md transition-shadow flex items-center gap-4">
                <span className="w-12 h-12 rounded-full flex items-center justify-center text-white font-heading font-semibold text-lg flex-shrink-0" style={{ backgroundColor: n.color }}>{n.name.charAt(0)}</span>
                <div className="flex-1 min-w-0"><p className="font-heading text-[#2E3A33]">{n.name}</p></div>
                <span className="text-sm font-semibold flex-shrink-0 text-green-600">Grátis</span>
              </button>
            ))}
            <button onClick={cancelFreeMode} className="text-xs text-[#7A8278] underline hover:text-[#5B8C7B]" data-testid="cancel-free-mode-btn">Voltar à marcação normal</button>
          </div>
        )}

        {/* STEP 2 — Choose slot */}
        {step === 2 && (
          <div data-testid="booking-step-slots">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-[#7A8278] mb-3"><CaretLeft size={14} /> Voltar</button>
            {nutri?.welcome_message && <div className="bg-white rounded-2xl border border-[#E8E3DB] p-3 mb-4 text-sm text-[#2E3A33]" style={{ borderLeftWidth: 4, borderLeftColor: accent }}>{nutri.welcome_message}</div>}
            {loadingSlots ? (
              <div className="text-center py-10"><Spinner size={32} className="mx-auto animate-spin text-[#5B8C7B]" /></div>
            ) : days.length === 0 ? (
              <p className="text-center text-[#A8AFA3] py-8">Sem horários disponíveis nas próximas semanas.</p>
            ) : (
              <>
                <p className="text-sm text-[#7A8278] mb-2">Escolhe o dia:</p>
                <div className="bg-white rounded-2xl border border-[#E8E3DB] p-4 mb-4" data-testid="booking-calendar">
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => goMonth(-1)} disabled={!canPrev} className="w-8 h-8 rounded-full flex items-center justify-center text-[#2E3A33] disabled:opacity-30 hover:bg-[#F6F5F1]" data-testid="cal-prev-btn"><CaretLeft size={16} /></button>
                    <span className="font-heading font-medium text-[#2E3A33]" data-testid="cal-month-label">{MONTHS_FULL[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
                    <button onClick={() => goMonth(1)} disabled={!canNext} className="w-8 h-8 rounded-full flex items-center justify-center text-[#2E3A33] disabled:opacity-30 hover:bg-[#F6F5F1]" data-testid="cal-next-btn"><CaretRight size={16} /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {CAL_DOW.map(d => <div key={d} className="text-center text-[10px] text-[#A8AFA3] font-medium py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {monthCells.map((cell, idx) => {
                      if (!cell) return <div key={`e${idx}`} />;
                      const iso = toISO(cell);
                      const available = availSet.has(iso);
                      const selected = selDate === iso;
                      const n = slotCountByDate[iso] || 0;
                      return (
                        <button key={iso} disabled={!available} onClick={() => { setSelDate(iso); setSelTime(''); }} data-testid={`booking-day-${iso}`}
                          title={available ? `${n} ${n === 1 ? 'horário disponível' : 'horários disponíveis'}` : undefined}
                          className={`aspect-square rounded-xl text-sm font-medium flex flex-col items-center justify-center gap-0 transition-colors ${selected ? 'text-white' : available ? 'bg-[#F6F5F1] text-[#2E3A33] hover:bg-[#ECE9E2]' : 'text-[#D5D2CA] cursor-not-allowed'}`}
                          style={selected ? { backgroundColor: accent } : {}}>
                          <span className="leading-none">{cell.getDate()}</span>
                          {available && <span className="text-[9px] font-normal leading-none mt-0.5" style={selected ? { opacity: 0.85 } : { color: accent }} data-testid={`booking-day-count-${iso}`}>{n} {n === 1 ? 'vaga' : 'vagas'}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {dayObj && (
                  <>
                    <p className="text-sm text-[#7A8278] mb-2">Horários para {fmtDay(selDate).day} {fmtDay(selDate).mon}:</p>
                    <div className="grid grid-cols-4 gap-2 mb-5">
                      {dayObj.slots.map(t => (
                        <button key={t} onClick={() => setSelTime(t)} data-testid={`booking-slot-${t}`}
                          className={`rounded-xl border py-2.5 text-sm font-medium ${selTime === t ? 'text-white border-transparent' : 'bg-white border-[#E8E3DB] text-[#2E3A33]'}`}
                          style={selTime === t ? { backgroundColor: accent } : {}}>{t}</button>
                      ))}
                    </div>
                  </>
                )}
                <button onClick={goToForm} disabled={!selDate || !selTime} className="w-full rounded-full py-3 text-white font-medium disabled:opacity-40" style={{ backgroundColor: accent }} data-testid="booking-continue-btn">Continuar</button>
              </>
            )}
          </div>
        )}

        {/* STEP 3 — Details + pay */}
        {step === 3 && mode === 'paid' && (
          <div data-testid="booking-step-form">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-[#7A8278] mb-3"><CaretLeft size={14} /> Voltar</button>
            <div className="bg-white rounded-2xl border border-[#E8E3DB] p-3 mb-4 text-sm text-[#2E3A33]">
              <strong>{nutri.name}</strong> · {fmtDay(selDate).wd}, {fmtDay(selDate).day} {fmtDay(selDate).mon} às {selTime} · {form.modality === 'online' ? 'Online' : 'Presencial'} · <span style={{ color: accent }}>{nutri.booking_price}€</span>
            </div>
            <div className="space-y-3 bg-white rounded-2xl border border-[#E8E3DB] p-4">
              <div><Label className="text-xs text-[#7A8278] mb-1 block">{isPediatric ? 'Nome de quem marca' : 'Nome completo'}</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="booking-name" /></div>
              {isPediatric && <div><Label className="text-xs text-[#7A8278] mb-1 block">Nome da criança</Label><Input value={form.child_name || ''} onChange={e => setForm({ ...form, child_name: e.target.value })} className="rounded-xl" data-testid="booking-child-name" /></div>}
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl" data-testid="booking-email" /></div>
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Telemóvel</Label><div className="flex gap-1"><CountryCodeSelect value={form.country_code || '+351'} onChange={v => setForm({ ...form, country_code: v })} testId="booking-country-code" /><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-xl flex-1" data-testid="booking-phone" /></div></div>
              <div data-testid="booking-modality-field"><Label className="text-xs text-[#7A8278] mb-1 block">Tipo de consulta</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[['presencial', 'Presencial (Guimarães)'], ['online', 'Online']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, modality: v })} aria-pressed={form.modality === v}
                      className={`rounded-xl border px-3 py-2 text-sm transition-colors ${form.modality === v ? 'text-white border-transparent' : 'bg-white text-[#2E3A33] border-[#E8E3DB]'}`}
                      style={form.modality === v ? { backgroundColor: accent } : undefined} data-testid={`booking-modality-${v}`}>{l}</button>
                  ))}
                </div>
                {form.modality === 'online' && <p className="text-[11px] text-[#7A8278] mt-1.5" data-testid="booking-modality-online-hint">Vais receber o link da videochamada por email.</p>}
              </div>
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Programa de interesse</Label>
                <select value={form.program || programs[0] || ''} onChange={e => setForm({ ...form, program: e.target.value })} className="w-full rounded-xl border border-[#E8E3DB] px-3 py-2 text-sm bg-white" data-testid="booking-program">
                  {programs.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {programDescriptions[form.program || programs[0]] && (
                  <p className="text-[11px] text-[#7A8278] mt-1.5" data-testid="booking-program-desc">{programDescriptions[form.program || programs[0]]}</p>
                )}
              </div>
              {(form.program || programs[0]) === 'Super Barriguinhas' && (
                <div data-testid="booking-dum-field"><Label className="text-xs text-[#7A8278] mb-1 block">Data da última menstruação (DUM) <span className="text-[#A8AFA3]">· opcional</span></Label>
                  <Input type="date" value={form.dum} onChange={e => setForm({ ...form, dum: e.target.value })} className="rounded-xl" data-testid="booking-dum" /></div>
              )}
              {(form.program || programs[0]) === 'Super Mamãs' && (
                <div data-testid="booking-delivery-field"><Label className="text-xs text-[#7A8278] mb-1 block">Data do parto <span className="text-[#A8AFA3]">· opcional</span></Label>
                  <Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} className="rounded-xl" data-testid="booking-delivery-date" /></div>
              )}
              <label className="flex items-start gap-2 text-[12px] text-[#2E3A33] cursor-pointer" data-testid="booking-privacy-label">
                <input type="checkbox" checked={privacyConsent} onChange={e => setPrivacyConsent(e.target.checked)} className="mt-0.5" data-testid="booking-privacy-consent" />
                <span>Li e aceito os <a href="/termos-e-condicoes" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="booking-terms-link">Termos de Serviço</a> e a <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="booking-privacy-link">Política de Privacidade</a>.</span>
              </label>
              <WithdrawalWaiverCheckbox
                checked={waiverConsent}
                onChange={setWaiverConsent}
                waiverText={waiverText}
                testId="booking-waiver-consent"
              />
              <button onClick={submit} disabled={submitting || !privacyConsent || !waiverConsent} className="w-full rounded-full py-3 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2" style={{ backgroundColor: accent }} data-testid="booking-pay-btn">
                {submitting ? <><Spinner size={16} className="animate-spin" /> A redirecionar...</> : `Pagar ${nutri.booking_price}€ e confirmar`}
              </button>
              {cancellationPolicy && cancellationPolicy.trim() && (
                <div className="text-[11px] text-[#7A8278] text-center" data-testid="booking-policy-notice">
                  Ao confirmar a marcação, aceitas a nossa{' '}
                  <button type="button" onClick={() => setPolicyOpen(o => !o)} className="underline text-[#657161]" data-testid="booking-policy-toggle">política de cancelamento</button>.
                  {policyOpen && (
                    <div className="mt-2 text-left bg-[#F6F5F1] rounded-xl p-3" data-testid="booking-policy-content">
                      <Markdown>{cancellationPolicy}</Markdown>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[10px] text-[#A8AFA3] text-center">Pagamento seguro via Stripe. O horário fica reservado durante o pagamento.</p>
            </div>
          </div>
        )}

        {/* STEP 3 (modo sessão gratuita) — Dados de contacto, sem pagamento */}
        {step === 3 && mode === 'free' && (
          <div data-testid="free-booking-step-form">
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-[#7A8278] mb-3"><CaretLeft size={14} /> Voltar</button>
            <div className="bg-white rounded-2xl border border-[#E8E3DB] p-3 mb-4 text-sm text-[#2E3A33]">
              <strong>{nutri.name}</strong> · {fmtDay(selDate).wd}, {fmtDay(selDate).day} {fmtDay(selDate).mon} às {selTime} · <span className="text-green-600 font-semibold">Grátis (20 min)</span>
            </div>
            <div className="space-y-3 bg-white rounded-2xl border border-[#E8E3DB] p-4">
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Nome completo</Label><Input value={freeForm.name} onChange={e => setFreeForm({ ...freeForm, name: e.target.value })} className="rounded-xl" data-testid="free-booking-name" /></div>
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Email</Label><Input type="email" value={freeForm.email} onChange={e => setFreeForm({ ...freeForm, email: e.target.value })} className="rounded-xl" data-testid="free-booking-email" /></div>
              <div><Label className="text-xs text-[#7A8278] mb-1 block">Telemóvel</Label><div className="flex gap-1"><CountryCodeSelect value={freeForm.country_code || '+351'} onChange={v => setFreeForm({ ...freeForm, country_code: v })} testId="free-booking-country-code" /><Input value={freeForm.phone} onChange={e => setFreeForm({ ...freeForm, phone: e.target.value })} className="rounded-xl flex-1" data-testid="free-booking-phone" /></div></div>
              <label className="flex items-start gap-2 text-[12px] text-[#2E3A33] cursor-pointer" data-testid="free-booking-privacy-label">
                <input type="checkbox" checked={freePrivacyConsent} onChange={e => setFreePrivacyConsent(e.target.checked)} className="mt-0.5" data-testid="free-booking-privacy-consent" />
                <span>Li e aceito os <a href="/termos-e-condicoes" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="free-booking-terms-link">Termos de Serviço</a> e a <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="underline text-[#657161]" data-testid="free-booking-privacy-link">Política de Privacidade</a>.</span>
              </label>
              <button onClick={submitFree} disabled={freeSubmitting || !freePrivacyConsent} className="w-full rounded-full py-3 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2 bg-green-600" data-testid="free-booking-confirm-btn">
                {freeSubmitting ? <><Spinner size={16} className="animate-spin" /> A confirmar...</> : 'Confirmar sessão gratuita'}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="max-w-2xl mx-auto px-4"><PublicFooter /></div>
    </div>
  );
}
